import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { customerLabel, money, timeOnly, vehicleLabel } from "../lib/format";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Layout";
import { Card, DataState, StatTile, StatusBadge, Table } from "../components/ui";
import { ClockWidget } from "../components/ClockWidget";
import { WalkInButton } from "../components/WalkInModal";
import type { DashboardData } from "../lib/types";

/**
 * The dashboard the guide's navigation table calls for is one screen, but
 * what an owner, a manager and a technician actually need from "the first
 * thing I see today" are different jobs: an owner wants the shop's whole
 * state, a manager wants today's operations, a technician wants their own
 * assigned work and a way to punch in. All three read the same
 * `/reports/dashboard` payload; the server already strips the figures a role
 * cannot see (build guide / 03), so this file only decides layout.
 */
export function DashboardPage() {
  const { user } = useAuth();

  const dashboard = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<DashboardData>("/reports/dashboard"),
  });

  if (user?.role === "TECHNICIAN") {
    return <WorkerDashboard data={dashboard.data} loading={dashboard.isLoading} error={dashboard.error} />;
  }
  if (user?.role === "OWNER") {
    return <OwnerDashboard data={dashboard.data} loading={dashboard.isLoading} error={dashboard.error} />;
  }
  return <ManagerDashboard data={dashboard.data} loading={dashboard.isLoading} error={dashboard.error} />;
}

type DashboardProps = { data?: DashboardData; loading: boolean; error: unknown };

function AppointmentsTable({ data }: { data: DashboardData }) {
  return (
    <Card title="Today's appointments">
      <DataState
        empty={data.appointmentsToday.length === 0}
        emptyMessage="No appointments booked for today."
      >
        <Table headers={["Time", "Customer", "Vehicle", "Concern", "Status"]}>
          {data.appointmentsToday.map((appt) => (
            <tr key={appt.id}>
              <td className="whitespace-nowrap px-3 py-2 font-medium tabular-nums">
                {timeOnly(appt.scheduledFor)}
              </td>
              <td className="px-3 py-2">{customerLabel(appt.customer)}</td>
              <td className="px-3 py-2 text-slate-600">{vehicleLabel(appt.vehicle)}</td>
              <td className="max-w-xs truncate px-3 py-2 text-slate-600">{appt.concern}</td>
              <td className="px-3 py-2">
                <StatusBadge status={appt.status} />
              </td>
            </tr>
          ))}
        </Table>
      </DataState>
    </Card>
  );
}

function ReconciliationAlert({ data }: { data: DashboardData }) {
  const { can } = useAuth();
  if (data.openReconciliationItems === 0 || !can("finance.reconcile")) return null;
  return (
    <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      <p className="font-medium">
        {data.openReconciliationItems} reconciliation exception(s) need attention
      </p>
      <p className="mt-1">
        Unmatched payments, missing fees or payouts that have not arrived.{" "}
        <Link to="/finance" className="font-medium underline">
          Open the reconciliation queue
        </Link>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Owner: the full shop
// ---------------------------------------------------------------------------

function OwnerDashboard({ data, loading, error }: DashboardProps) {
  const { user, can } = useAuth();

  const sales = useQuery({
    queryKey: ["daily-sales", "dashboard-snapshot"],
    queryFn: () => api.get<{ totals: { net: number; tax: number; gross: number } }>("/reports/daily-sales"),
    enabled: can("reports.sales"),
  });

  return (
    <>
      <PageHeader
        title={`Good day, ${user?.firstName}`}
        description={
          data
            ? `The whole shop at a glance. Times shown in ${data.timezone}.`
            : undefined
        }
        action={can("jobs.write") ? <WalkInButton /> : undefined}
      />

      <DataState loading={loading} error={error}>
        {data && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Appointments today"
                value={String(data.appointmentsToday.length)}
                sub="Scheduled and confirmed"
              />
              <StatTile label="Open jobs" value={String(data.openJobs)} sub="Not yet delivered" />
              <StatTile
                label="Unpaid invoices"
                value={data.unpaidInvoices.outstanding !== null ? money(data.unpaidInvoices.outstanding) : "—"}
                sub={`${data.unpaidInvoices.count} invoice(s) outstanding`}
                tone={
                  data.unpaidInvoices.outstanding !== null && data.unpaidInvoices.outstanding > 0
                    ? "warn"
                    : "neutral"
                }
              />
              <StatTile
                label="Collected today"
                value={data.cashCollectedToday !== null ? money(data.cashCollectedToday) : "—"}
                sub={`${data.paymentsToday} payment(s). Not the same as sales.`}
                tone="good"
              />
            </div>

            {sales.data && (
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <StatTile
                  label="Net sales (30d)"
                  value={money(sales.data.totals.net)}
                  sub="Excludes sales tax"
                />
                <StatTile
                  label="Sales tax collected (30d)"
                  value={money(sales.data.totals.tax)}
                  sub="A liability, not revenue"
                />
                <StatTile label="Invoiced gross (30d)" value={money(sales.data.totals.gross)} />
              </div>
            )}

            <ReconciliationAlert data={data} />

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <AppointmentsTable data={data} />

              <Card title="Run the shop">
                <ul className="space-y-3 text-sm">
                  <li>
                    <Link to="/board" className="font-medium text-teal-700 hover:underline">
                      Work board
                    </Link>
                    <p className="text-slate-500">Move jobs between lanes and assign technicians.</p>
                  </li>
                  <li>
                    <Link to="/reports" className="font-medium text-teal-700 hover:underline">
                      Job margins
                    </Link>
                    <p className="text-slate-500">
                      Gross profit by job, with missing costs flagged rather than hidden.
                    </p>
                  </li>
                  <li>
                    <Link to="/timeclock" className="font-medium text-teal-700 hover:underline">
                      Payroll and time clock
                    </Link>
                    <p className="text-slate-500">
                      Everyone's hours and an estimated payroll cost for the period.
                    </p>
                  </li>
                  <li>
                    <Link to="/settings" className="font-medium text-teal-700 hover:underline">
                      Team and settings
                    </Link>
                    <p className="text-slate-500">Roles, pay rates, tax configuration, providers.</p>
                  </li>
                  <li>
                    <Link to="/parts-catalog" className="font-medium text-teal-700 hover:underline">
                      Parts and vehicles
                    </Link>
                    <p className="text-slate-500">Manage the parts catalog and vehicle fitment.</p>
                  </li>
                </ul>
              </Card>
            </div>
          </>
        )}
      </DataState>
    </>
  );
}

// ---------------------------------------------------------------------------
// Manager (and service advisor / bookkeeper): today's operations
// ---------------------------------------------------------------------------

function ManagerDashboard({ data, loading, error }: DashboardProps) {
  const { user, can } = useAuth();

  return (
    <>
      <PageHeader
        title={`Good day, ${user?.firstName}`}
        description={
          data
            ? `Today's work at ${user?.location?.name ?? user?.organization.name}. Times shown in ${data.timezone}.`
            : undefined
        }
        action={can("jobs.write") ? <WalkInButton /> : undefined}
      />

      <DataState loading={loading} error={error}>
        {data && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Appointments today"
                value={String(data.appointmentsToday.length)}
                sub="Scheduled and confirmed"
              />
              <StatTile label="Open jobs" value={String(data.openJobs)} sub="Not yet delivered" />
              <StatTile
                label="Unpaid invoices"
                value={data.unpaidInvoices.outstanding !== null ? money(data.unpaidInvoices.outstanding) : "—"}
                sub={`${data.unpaidInvoices.count} invoice(s) outstanding`}
                tone={
                  data.unpaidInvoices.outstanding !== null && data.unpaidInvoices.outstanding > 0
                    ? "warn"
                    : "neutral"
                }
              />
              {can("finance.read") && data.cashCollectedToday !== null ? (
                <StatTile
                  label="Collected today"
                  value={money(data.cashCollectedToday)}
                  sub={`${data.paymentsToday} payment(s). Not the same as sales.`}
                  tone="good"
                />
              ) : (
                <StatTile
                  label="Payments today"
                  value={String(data.paymentsToday)}
                  sub="Amounts are restricted for your role"
                />
              )}
            </div>

            <ReconciliationAlert data={data} />

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <AppointmentsTable data={data} />

              <Card title="Where to go next">
                <ul className="space-y-3 text-sm">
                  <li>
                    <Link to="/board" className="font-medium text-teal-700 hover:underline">
                      Work board
                    </Link>
                    <p className="text-slate-500">Move jobs between lanes and assign technicians.</p>
                  </li>
                  <li>
                    <Link to="/customers" className="font-medium text-teal-700 hover:underline">
                      Customers and vehicles
                    </Link>
                    <p className="text-slate-500">
                      Search by name, phone, VIN or plate, and open a customer's profile.
                    </p>
                  </li>
                  {can("payments.collect") && (
                    <li>
                      <Link to="/pos" className="font-medium text-teal-700 hover:underline">
                        Point of sale
                      </Link>
                      <p className="text-slate-500">
                        Collect a balance by card, QR code or cash and issue a receipt.
                      </p>
                    </li>
                  )}
                  {can("reports.costs") && (
                    <li>
                      <Link to="/reports" className="font-medium text-teal-700 hover:underline">
                        Job margins
                      </Link>
                      <p className="text-slate-500">
                        Gross profit by job, with missing costs flagged rather than hidden.
                      </p>
                    </li>
                  )}
                </ul>
              </Card>
            </div>
          </>
        )}
      </DataState>
    </>
  );
}

// ---------------------------------------------------------------------------
// Worker (technician): my jobs and the clock
// ---------------------------------------------------------------------------

function WorkerDashboard({ data, loading, error }: DashboardProps) {
  const { user } = useAuth();

  return (
    <>
      <PageHeader
        title={`Good day, ${user?.firstName}`}
        description={data ? `Your assigned work. Times shown in ${data.timezone}.` : undefined}
      />

      <DataState loading={loading} error={error}>
        {data && (
          <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
            <div className="space-y-4">
              <ClockWidget />
              <div className="grid grid-cols-2 gap-4">
                <StatTile label="My open tasks" value={String(data.myOpenTasks.length)} />
                <StatTile label="Open jobs, shop-wide" value={String(data.openJobs)} />
              </div>
            </div>

            <div className="space-y-5">
              <Card title="My open tasks">
                <DataState
                  empty={data.myOpenTasks.length === 0}
                  emptyMessage="Nothing assigned to you right now."
                >
                  <Table headers={["Job", "Vehicle", "Task", "Status", ""]}>
                    {data.myOpenTasks.map((t) => (
                      <tr key={t.id}>
                        <td className="px-3 py-2 font-mono text-xs">{t.repairOrder.number}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {[t.repairOrder.vehicle.year, t.repairOrder.vehicle.make, t.repairOrder.vehicle.model]
                            .filter(Boolean)
                            .join(" ")}
                          {t.repairOrder.vehicle.plate ? ` (${t.repairOrder.vehicle.plate})` : ""}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{t.description}</td>
                        <td className="px-3 py-2">
                          <StatusBadge status={t.status} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Link
                            to={`/jobs/${t.repairOrder.id}`}
                            className="text-sm text-teal-700 hover:underline"
                          >
                            Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </Table>
                </DataState>
              </Card>

              <AppointmentsTable data={data} />
            </div>
          </div>
        )}
      </DataState>
    </>
  );
}
